/**
 * ClaimStatusBanner Styles
 * SESSION_331: Non-Existent Business Claim Portal Status
 *
 * Distinctive, refined styling for claim status banners.
 * Design direction: Clean, geometric, professional with subtle depth.
 */

import { css, keyframes } from '@emotion/react';

/**
 * Subtle pulse animation for the pending indicator
 */
export const pulseKeyframes = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
`;

/**
 * Shimmer animation for pending progress bar
 */
export const shimmerKeyframes = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

/**
 * Slide in animation for banner entrance
 */
export const slideInKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Base banner container styles
 */
export const bannerContainerStyles = css`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  animation: ${slideInKeyframes} 0.4s ease-out;
`;

/**
 * Pending variant styles - sophisticated blue/indigo
 */
export const pendingBannerStyles = css`
  background: linear-gradient(135deg, #4158D0 0%, #5B6DD6 50%, #C850C0 100%);
  background-size: 200% 200%;
  box-shadow:
    0 4px 20px rgba(65, 88, 208, 0.25),
    0 2px 8px rgba(65, 88, 208, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
`;

/**
 * Rejected variant styles - warm coral/rose
 */
export const rejectedBannerStyles = css`
  background: linear-gradient(135deg, #E8585B 0%, #D94857 50%, #BE3455 100%);
  box-shadow:
    0 4px 20px rgba(217, 72, 87, 0.25),
    0 2px 8px rgba(217, 72, 87, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
`;

/**
 * Inner content wrapper with subtle overlay
 */
export const contentWrapperStyles = css`
  position: relative;
  z-index: 1;
  padding: 20px 24px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
    z-index: -1;
  }
`;

/**
 * Geometric accent shape in background
 */
export const geometricAccentStyles = css`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 140px;
  height: 140px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Secondary geometric accent
 */
export const geometricAccentSecondaryStyles = css`
  position: absolute;
  bottom: -40px;
  right: 60px;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Icon container with subtle glow
 */
export const iconContainerStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  flex-shrink: 0;
`;

/**
 * Pending icon with pulse animation
 */
export const pendingIconStyles = css`
  animation: ${pulseKeyframes} 2s ease-in-out infinite;
`;

/**
 * Title text styles
 */
export const titleStyles = css`
  font-family: 'Comfortaa', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

/**
 * Message text styles
 */
export const messageStyles = css`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.5;
`;

/**
 * Progress bar container for pending state
 */
export const progressBarContainerStyles = css`
  position: relative;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 16px;
`;

/**
 * Animated shimmer overlay for progress bar
 */
export const progressBarShimmerStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%
  );
  animation: ${shimmerKeyframes} 2s ease-in-out infinite;
`;

/**
 * Support link button styles
 */
export const supportLinkStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/**
 * Business name highlight styles
 */
export const businessNameStyles = css`
  font-weight: 600;
  color: #ffffff;
`;

/**
 * Rejection reason box styles
 */
export const rejectionReasonBoxStyles = css`
  margin-top: 12px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border-left: 3px solid rgba(255, 255, 255, 0.5);
`;

/**
 * Rejection reason label
 */
export const rejectionReasonLabelStyles = css`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 4px 0;
`;

/**
 * Rejection reason text
 */
export const rejectionReasonTextStyles = css`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-style: italic;
`;
